import { useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "../../../../shared/ui/button/Button";
import { usePendingCenterConsent } from "../../../center";

interface Props {
    centerName: string;
}

/** Why one person could not be brought in, shown on their own row. */
interface Refusal {
    patientId: string;
    /** Their group has people outside the centre: it comes in whole or not at all. */
    splitsGroup: boolean;
}

const SPLITS_GROUP = "consent_splits_group";

/**
 * The people a therapist brought with them when they joined a centre.
 *
 * Their consent named the therapist, alone. Until each of them agrees to the
 * centre holding their record, the centre's administrator cannot see them —
 * and the person who asks is the professional they already trust, which is
 * why this lives with the therapist's own centre details and not in the
 * administration.
 *
 * A group is all the centre's or all the therapist's own. Somebody whose group
 * still has people outside is refused alone, and offered the group instead:
 * the therapist confirms that everyone in it agreed, and they come in together.
 */
export default function CenterConsentCard({ centerName }: Props) {
    const { t } = useTranslation();
    const { patients, loading, recordConsent } = usePendingCenterConsent();
    const [refusal, setRefusal] = useState<Refusal | null>(null);

    const record = (patientId: string, withGroup = false) => {
        setRefusal(null);
        recordConsent.mutate(
            { patientId, withGroup },
            {
                onError: (err: unknown) => {
                    const detail = (err as { response?: { data?: { detail?: string } } })
                        ?.response?.data?.detail;
                    setRefusal({ patientId, splitsGroup: detail === SPLITS_GROUP });
                },
            }
        );
    };

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
            <h3 className="mb-1 text-lg font-semibold text-content-primary">
                {t("center.consent.title")}
            </h3>
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                {t("center.consent.subtitle", { center: centerName })}
            </p>

            <p className="mb-5 rounded-xl bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                {t("center.consent.placeholderNotice")}
            </p>

            {loading && <p className="text-sm text-gray-500">{t("common.loading")}</p>}

            {!loading && patients.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("center.consent.empty")}
                </p>
            )}

            <ul className="flex flex-col divide-y divide-gray-100 dark:divide-gray-800">
                {patients.map((patient) => (
                    <li
                        key={patient.patient_id}
                        className="flex flex-wrap items-center gap-3 py-3 text-sm"
                    >
                        <span className="text-content-primary">
                            {patient.first_name} {patient.last_name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {t("center.consent.sessions", { count: patient.session_count })}
                        </span>
                        <Button
                            size="sm"
                            className="ml-auto"
                            onClick={() => record(patient.patient_id)}
                            disabled={recordConsent.isPending}
                        >
                            {t("center.consent.record")}
                        </Button>
                        {refusal?.patientId === patient.patient_id && (
                            <div className="flex w-full flex-wrap items-center gap-3">
                                <p
                                    role="alert"
                                    className="text-sm text-red-600 dark:text-red-400"
                                >
                                    {refusal.splitsGroup
                                        ? t("center.consent.errorSplitsGroup")
                                        : t("center.consent.errorGeneric")}
                                </p>
                                {refusal.splitsGroup && (
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="ml-auto"
                                        onClick={() => record(patient.patient_id, true)}
                                        disabled={recordConsent.isPending}
                                    >
                                        {t("center.consent.recordWithGroup")}
                                    </Button>
                                )}
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </section>
    );
}
